import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Logo from '../common/Logo';

export default function Footer() {
  const location = useLocation();
  const currentYear = new Date().getFullYear();
  const { t } = useTranslation();

  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== '/') {
      window.REACT_APP_NAVIGATE('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <footer id="ressourcen" className="bg-slate-50 border-t border-slate-200 text-[#0F172A] relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 left-0 w-48 h-48 md:w-96 md:h-96 bg-gradient-to-r from-[#10B981] to-[#1E3A8A] rounded-full blur-3xl pointer-events-none opacity-50"></div>
      <div className="absolute bottom-0 right-0 w-48 h-48 md:w-96 md:h-96 bg-emerald-100 rounded-full blur-3xl pointer-events-none opacity-50"></div>

      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 py-12 md:py-16 lg:py-20 relative z-10">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 mb-12 md:mb-16">
          {/* Company Info */}
          <div className="sm:col-span-2 lg:col-span-1">
            {/* Brand */}
            <Logo
              className="flex items-center gap-2 mb-6"
              iconClassName="w-10 h-10"
              textClassName="text-xl font-bold tracking-tight"
            />
            <p className="text-sm md:text-base text-slate-600 mb-6 leading-relaxed" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              {t('footer.description')}
            </p>
          </div>

          {/* Product Links */}
          <div>
            <h3 className="text-base md:text-lg font-bold mb-4 md:mb-6" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
              {t('footer.product')}
            </h3>
            <ul className="space-y-2.5 md:space-y-3">
              <li>
                <button
                  onClick={() => scrollToSection('funktionen')}
                  className="text-sm md:text-base text-slate-500 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#10B981] hover:to-[#1E3A8A] transition-colors whitespace-nowrap"
                  style={{ fontFamily: 'DM Sans, sans-serif' }}
                >
                  {t('footer.features')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('preise')}
                  className="text-sm md:text-base text-slate-500 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#10B981] hover:to-[#1E3A8A] transition-colors whitespace-nowrap"
                  style={{ fontFamily: 'DM Sans, sans-serif' }}
                >
                  {t('footer.pricing')}
                </button>
              </li>
              <li>
                <Link
                  to="/case-studies"
                  className="text-sm md:text-base text-slate-500 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#10B981] hover:to-[#1E3A8A] transition-colors whitespace-nowrap"
                  style={{ fontFamily: 'DM Sans, sans-serif' }}
                >
                  {t('footer.caseStudies')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h3 className="text-base md:text-lg font-bold mb-4 md:mb-6" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
              {t('footer.support')}
            </h3>
            <ul className="space-y-2.5 md:space-y-3">
              <li>
                <Link
                  to="/blog"
                  className="text-sm md:text-base text-slate-500 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#10B981] hover:to-[#1E3A8A] transition-colors whitespace-nowrap"
                  style={{ fontFamily: 'DM Sans, sans-serif' }}
                >
                  {t('footer.blog')}
                </Link>
              </li>
              <li>
                <Link
                  to="/hilfe-center"
                  className="text-sm md:text-base text-slate-500 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#10B981] hover:to-[#1E3A8A] transition-colors whitespace-nowrap"
                  style={{ fontFamily: 'DM Sans, sans-serif' }}
                >
                  {t('footer.helpCenter')}
                </Link>
              </li>
              <li>
                <a
                  href="mailto:support@reputrust.ai"
                  className="text-sm md:text-base text-slate-500 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#10B981] hover:to-[#1E3A8A] transition-colors whitespace-nowrap"
                  style={{ fontFamily: 'DM Sans, sans-serif' }}
                >
                  {t('footer.contact')}
                </a>
              </li>
              <li>
                <Link
                  to="/partner-login"
                  className="text-sm md:text-base text-slate-500 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#10B981] hover:to-[#1E3A8A] transition-colors whitespace-nowrap flex items-center gap-1.5"
                  style={{ fontFamily: 'DM Sans, sans-serif' }}
                >
                  Partner Portal
                </Link>
              </li>
              <li>
                <Link
                  to="/manage-subscription"
                  className="text-sm md:text-base text-slate-500 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#10B981] hover:to-[#1E3A8A] transition-colors whitespace-nowrap"
                  style={{ fontFamily: 'DM Sans, sans-serif' }}
                >
                  Manage Subscription
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="text-base md:text-lg font-bold mb-4 md:mb-6" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
              {t('footer.legal')}
            </h3>
            <ul className="space-y-2.5 md:space-y-3">
              <li>
                <Link
                  to="/impressum"
                  className="text-sm md:text-base text-slate-500 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#10B981] hover:to-[#1E3A8A] transition-colors whitespace-nowrap"
                  style={{ fontFamily: 'DM Sans, sans-serif' }}
                >
                  {t('footer.imprint')}
                </Link>
              </li>
              <li>
                <Link
                  to="/datenschutz"
                  className="text-sm md:text-base text-slate-500 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#10B981] hover:to-[#1E3A8A] transition-colors whitespace-nowrap"
                  style={{ fontFamily: 'DM Sans, sans-serif' }}
                >
                  {t('footer.privacy')}
                </Link>
              </li>
              <li>
                <Link
                  to="/agb"
                  className="text-sm md:text-base text-slate-500 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#10B981] hover:to-[#1E3A8A] transition-colors whitespace-nowrap"
                  style={{ fontFamily: 'DM Sans, sans-serif' }}
                >
                  {t('footer.terms')}
                </Link>
              </li>
              <li>
                <Link
                  to="/cookie-richtlinie"
                  className="text-sm md:text-base text-slate-500 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#10B981] hover:to-[#1E3A8A] transition-colors whitespace-nowrap"
                  style={{ fontFamily: 'DM Sans, sans-serif' }}
                >
                  {t('footer.cookies')}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-6 md:pt-8 border-t border-slate-200">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <p className="text-xs md:text-sm text-slate-500 text-center" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              © {currentYear} REPUTRUST. Alle Rechte vorbehalten.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
